import { LogIn, Package, ShieldAlert } from 'lucide-react'

import { de } from '../i18n/de'

/**
 * Shown instead of the board when SSO is on and nobody is signed in.
 *
 * Deliberately not a redirect: bouncing straight to GitLab makes a failed login loop
 * invisibly, and the reason a callback failed (`?auth_error=`) would never be readable.
 */
export function LoginScreen({
  onSignIn,
  error,
  configured = true,
}: {
  onSignIn: () => void
  error?: string | null
  configured?: boolean
}) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-950">
          <Package className="h-6 w-6 text-indigo-600 dark:text-indigo-400" aria-hidden />
        </div>
        <h1 className="text-lg font-semibold">{de.app.title}</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{de.auth.intro}</p>

        {error ? (
          <p
            role="alert"
            className="mt-4 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-left text-sm text-rose-700 dark:bg-rose-950/50 dark:text-rose-300"
          >
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{error}</span>
          </p>
        ) : null}

        {configured ? (
          <button
            type="button"
            onClick={onSignIn}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-500 px-4 py-2.5 text-sm font-medium text-slate-950 transition hover:bg-indigo-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            <LogIn className="h-4 w-4" aria-hidden />
            {de.auth.signIn}
          </button>
        ) : (
          <p className="mt-6 rounded-lg bg-amber-50 px-3 py-2 text-left text-sm text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            {de.auth.notConfigured}
          </p>
        )}
      </div>
    </div>
  )
}
